import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getConditionChildrenKey from 'editor/src/store/design/reducer/getConditionChildrenKey';
import getElementParentKeys from 'editor/src/store/design/reducer/getElementParentKeys';
import {
  addElementToConditionAction,
  addElementToConditionGroupAction,
  addMediaElementAction,
  removeMediaElementAction,
  updateMediaElementAction,
} from 'editor/src/store/design/slice';
import {
  ConditionGroup,
  conditionGroupChildenKey,
  MediaElement,
  SlotFormat,
  SlotFrame,
  SlotLayout,
  Spread,
} from 'editor/src/store/design/types';
import cloneSlotElement, { SlotConditionMap } from 'editor/src/store/design/util/slotLayout/cloneSlotElement';
import { getUsedSlotFormats } from 'editor/src/store/design/util/slotLayout/computeSlotFrames';
import isSharedOutsideLayout from 'editor/src/store/design/util/slotLayout/isSharedOutsideLayout';
import { getActiveSlotCount } from 'editor/src/store/design/util/slotLayout/slotFrameVisibility';
import {
  getSlotFrameElements,
  getSlotTemplateFrame,
  isElementInFrame,
  isHandPlacedSlotElement,
} from 'editor/src/store/design/util/slotLayout/slotLayoutElements';
import {
  slotProductBase,
  slotProductId,
  slotTemplateSharedFieldId,
} from 'editor/src/store/design/util/slotLayout/slotLayoutIds';
import { getSlotTransform, transformSlotGeometry } from 'editor/src/store/design/util/slotLayout/transformSlotElement';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';
import { buildAdvPersoPluginsUpdate, getAdvPersoDataFromElement } from 'editor/src/store/plugins/utils/advPersoUtils';

import { elementUuids } from 'editor/src/util/uuids';

import updateHiddenElementsOperation from './updateHiddenElementsOperation';

function getConditionMap(
  conditionGroup: ConditionGroup | undefined,
  layout: SlotLayout,
  fromProduct: number,
  toProduct: number,
): SlotConditionMap | undefined {
  const from = conditionGroup?.conditions[layout.productConditionIds[fromProduct - 1]];
  const to = conditionGroup?.conditions[layout.productConditionIds[toProduct - 1]];
  if (!from || !to) {
    return undefined;
  }
  const optionIds = new Map<string, string>();
  from.options.forEach((option, index) => {
    const target = to.options[index];
    if (target) {
      optionIds.set(option.id, target.id);
    }
  });
  return { from: from.id, to: to.id, optionIds };
}

/**
 * Copies the template frame's layers into every other frame of the format. The template is
 * the first product's frame; anything the author placed inside it joins it first, so the slot
 * is designed on the page like any other content. Picture layers already exist in every frame
 * and only take the template's geometry; every other layer is replaced by a fresh copy, so the
 * template is the one place to change a slot.
 */
const applySlotTemplateOperation =
  (spreadIndex: number, layoutId: string, columns: SlotFormat, saveUndo = true) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const getSpread = (): Spread | undefined => getState().design.designData?.spreads[spreadIndex];
    const spread = getSpread();
    const layout = spread?.slotLayouts?.find((item) => item.id === layoutId);
    const template = layout && getSlotTemplateFrame(layout, columns, spread?.pages[0].groups.media);
    if (!spread || !layout || !template) {
      return;
    }

    batch(() => {
      if (saveUndo) {
        dispatch(saveUndoRedoStateOperation('Apply slot design'));
      }

      // Only the slot on the page takes in loose layers: frames of different formats overlap,
      // and the one the author sees is the one being designed.
      const activeCount = getActiveSlotCount(layout, spread.conditionGroup);
      const templateOnPage = activeCount !== undefined && template.counts.includes(activeCount);
      spread.pages[0].groups.media?.forEach((element, elementIndex) => {
        if (templateOnPage && !element.slotFrame && isElementInFrame(element, template)) {
          dispatch(
            updateMediaElementAction({
              elementAddress: { spreadIndex, pageIndex: 0, elementIndex },
              elementUpdate: { slotFrame: { layoutId, frameId: template.id } },
            }),
          );
        }
      });

      // A buyer-typed text in the template holds one value with its copies, and a collection
      // layer one pick, so each needs a shared id (a link group for pictures) before anything
      // is copied from it. A layer named like one in another format's template takes that
      // one's base id, suffixed with this template's product: a product's price is one value
      // whether the product sits in a wide or a square slot, and the two templates may belong
      // to different products. An id the author set by hand is suffixed the same way, so the
      // other format's template can share with it, unless the value is also on a layer outside
      // the layout: that is one value for every product. A text that varies by picklist holds
      // the author's strings, not the buyer's value, so it gets no shared id; nor does a text
      // printing a code-set column, which each unit fills from its own row.
      const adopted = getSpread()!;
      const sharedOutside = (id: string) => isSharedOutsideLayout(getState().design.designData!, id, layoutId);
      const sharedByName = new Map<string, string>();
      getUsedSlotFormats(layout.rows)
        .filter((format) => format !== columns)
        .forEach((format) => {
          const sibling = getSlotTemplateFrame(layout, format, adopted.pages[0].groups.media);
          if (sibling) {
            getSlotFrameElements(adopted, spreadIndex, layoutId, sibling.id).forEach(({ element }) => {
              const id = isHandPlacedSlotElement(element) ? getSlotSharedId(element) : undefined;
              if (id) {
                sharedByName.set(element.name, slotProductBase(id));
              }
            });
          }
        });
      getSlotFrameElements(adopted, spreadIndex, layoutId, template.id).forEach(({ element, address }) => {
        const text =
          element.type === 'text' &&
          !element.personalizationLocked &&
          !element.extra.textVariants &&
          !element.extra.variableColumn;
        const collection = element.type === 'image' ? getAdvPersoDataFromElement(element) : undefined;
        if (!text && !collection) {
          return;
        }
        const own = getSlotSharedId(element);
        if (own && sharedOutside(own)) {
          return;
        }
        const base =
          sharedByName.get(element.name) ??
          (own ? slotProductBase(own) : slotTemplateSharedFieldId(layoutId, element.uuid));
        const sharedId = sharedOutside(base) ? base : slotProductId(base, template.product);
        if (sharedId === own) {
          return;
        }
        if (element.type === 'text') {
          dispatch(
            updateMediaElementAction({
              elementAddress: address,
              elementUpdate: { extra: { ...element.extra, sharedFieldId: sharedId } },
            }),
          );
        } else if (element.type === 'image' && collection) {
          dispatch(
            updateMediaElementAction({
              elementAddress: address,
              elementUpdate: { plugins: buildAdvPersoPluginsUpdate(element, { ...collection, linkGroupId: sharedId }) },
            }),
          );
        }
      });

      const current = getSpread()!;
      const { conditionGroup } = current;
      const templateElements = getSlotFrameElements(current, spreadIndex, layoutId, template.id).map((e) => e.element);
      const topLevel = new Set(
        (conditionGroup?.children[conditionGroupChildenKey] ?? [])
          .filter((child) => child.type !== 'condition')
          .map((child) => child.id),
      );
      const templateConditionId = layout.productConditionIds[template.product - 1];

      layout.frames
        .filter((frame) => frame.columns === columns && frame.id !== template.id)
        .forEach((target) => {
          dispatch(
            replaceFrameContent(
              spreadIndex,
              layout,
              template,
              target,
              templateElements,
              conditionGroup,
              topLevel,
              templateConditionId,
            ),
          );
        });

      dispatch(updateHiddenElementsOperation(spreadIndex));
    });
  };

/** The id a layer shares its buyer value under: a text's shared field, a collection's link group. */
function getSlotSharedId(element: MediaElement): string | undefined {
  if (element.type === 'text') {
    return element.extra.sharedFieldId;
  }
  return element.type === 'image' ? getAdvPersoDataFromElement(element)?.linkGroupId : undefined;
}

const replaceFrameContent =
  (
    spreadIndex: number,
    layout: SlotLayout,
    template: SlotFrame,
    target: SlotFrame,
    templateElements: MediaElement[],
    conditionGroup: ConditionGroup | undefined,
    topLevel: Set<number>,
    templateConditionId: string | undefined,
  ) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const spread = () => getState().design.designData!.spreads[spreadIndex];
    const conditions = getConditionMap(conditionGroup, layout, template.product, target.product);
    const transform = getSlotTransform(template, target);

    getSlotFrameElements(spread(), spreadIndex, layout.id, target.id)
      .filter(({ element }) => !element.slotFrame?.fileSetId)
      .sort((a, b) => b.address.elementIndex - a.address.elementIndex)
      .forEach(({ address }) => dispatch(removeMediaElementAction(address)));

    const media = () => spread().pages[0].groups.media ?? [];
    const targetPicture = (fileSetId: string) =>
      getSlotFrameElements(spread(), spreadIndex, layout.id, target.id).find(
        (item) => item.element.slotFrame?.fileSetId === fileSetId,
      );

    // Copies keep the template's stacking: each lands right above the frame's counterpart of
    // the template layer below it, so a backdrop under the template's picture stays under the
    // frame's picture too. The lowest one goes under the first picture above it or, with no
    // picture to go by, above its template layer and the frame's own layers, so a layer the
    // author stacked over the slots stays over the copies and the copy is seen.
    let previous: number | undefined;
    templateElements.forEach((element, position) => {
      const parents = conditionGroup ? [...getElementParentKeys(conditionGroup, element.uuid)] : [];

      if (element.slotFrame?.fileSetId) {
        const picture = targetPicture(element.slotFrame.fileSetId);
        if (picture) {
          dispatch(
            updateMediaElementAction({
              elementAddress: picture.address,
              elementUpdate: transformSlotGeometry(element, transform),
            }),
          );
          previous = picture.element.uuid;
        }
        return;
      }

      const shared = getSlotSharedId(element);
      const clone = cloneSlotElement(element, {
        uuid: elementUuids.generate(),
        layoutId: layout.id,
        template,
        target,
        conditions,
        keepSharedId: !!shared && isSharedOutsideLayout(getState().design.designData!, shared, layout.id),
      });
      let elementIndex: number | undefined;
      if (previous !== undefined) {
        elementIndex = media().findIndex((item) => item.uuid === previous) + 1;
      } else {
        const pictureAbove = templateElements
          .slice(position + 1)
          .map((item) => item.slotFrame?.fileSetId && targetPicture(item.slotFrame.fileSetId))
          .find(Boolean);
        const below = media().reduce(
          (max, item, index) =>
            item.uuid === element.uuid ||
            (item.slotFrame?.layoutId === layout.id && item.slotFrame.frameId === target.id)
              ? Math.max(max, index)
              : max,
          -1,
        );
        if (pictureAbove) {
          elementIndex = pictureAbove.address.elementIndex;
        } else if (below !== -1) {
          elementIndex = below + 1;
        }
      }
      dispatch(addMediaElementAction({ spreadIndex, pageIndex: 0, element: clone, elementIndex }));
      previous = clone.uuid;

      parents.forEach((parent) => {
        const mapped = parent.conditionId === templateConditionId && conditions;
        const conditionId = mapped ? conditions.to : parent.conditionId;
        const optionId = mapped ? conditions.optionIds.get(parent.optionId) : parent.optionId;
        if (conditionId && optionId && conditionGroup?.children[getConditionChildrenKey(conditionId, optionId)]) {
          dispatch(addElementToConditionAction({ address: { spreadIndex, conditionId }, optionId, element: clone }));
        }
      });
      if (topLevel.has(element.uuid) && !clone.personalizationLocked) {
        dispatch(addElementToConditionGroupAction({ spreadIndex, element: clone }));
      }
    });
  };

export default applySlotTemplateOperation;
